import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Shared, single-fetch news unread tracker.
 *
 * Both the floating news orb (`FloatingNewsWidget`) and the sidebar
 * News nav item subscribe to the same backing store, so the unread
 * badge updates everywhere at once and we only hit `/api/news?limit=5`
 * once per 5-min window — no matter how many components mount.
 *
 * "Unseen" means: stories newer than the link the user last opened the
 * popover on (tracked in localStorage). Calling `markAllSeen()` (the
 * widget does this on open) anchors the cursor to the freshest story.
 */

const SEEN_KEY = "odyssey_news_seen_link";
const REFRESH_MS = 5 * 60 * 1000;

// Module-level singleton — survives component remounts.
let cache = { items: [], lastFetched: 0 };
let interval = null;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

const computeUnseen = (items) => {
    const lastSeen = localStorage.getItem(SEEN_KEY) || "";
    if (!items?.length) return 0;
    if (!lastSeen) return items.length;
    let n = 0;
    for (const it of items) {
        if (it.link === lastSeen) break;
        n += 1;
    }
    return n;
};

const refresh = async () => {
    try {
        const r = await api.get("/news?limit=5");
        cache = { items: r.data?.items || [], lastFetched: Date.now() };
        notify();
    } catch {
        /* silent — keep last known items */
    }
};

const startPolling = () => {
    if (interval) return;
    refresh();
    interval = setInterval(refresh, REFRESH_MS);
};

export const useNewsUnread = () => {
    // Cheap force-rerender on notify — we don't need the value, just a tick.
    const [, setTick] = useState(0);

    useEffect(() => {
        const fn = () => setTick((v) => v + 1);
        listeners.add(fn);
        startPolling();
        return () => {
            listeners.delete(fn);
        };
    }, []);

    const markAllSeen = useCallback(() => {
        const topLink = cache.items[0]?.link;
        if (!topLink) return;
        localStorage.setItem(SEEN_KEY, topLink);
        notify();
    }, []);

    return {
        items: cache.items,
        unseenCount: computeUnseen(cache.items),
        markAllSeen,
        refresh,
    };
};
